import type { EChartsOption } from 'echarts';

import { FONT, SEMANTIC_COLOR } from '@/shared/design-tokens/design-tokens';
import { formatMetricValue } from '@/shared/lib/utils';

import { createMetricChartSeries } from './createMetricChartSeries';
import { createTooltip } from './createTooltip';

export type PodMetricLineChartPodHistory = {
  pod: string;
  data: { timestamp: number; value: number }[];
  color: string;
};

type CreatePodMetricLineChartOptionInput = {
  podHistory: PodMetricLineChartPodHistory[];
  unit: string;
};

const createBaseAxisLabel = () => ({
  color: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL5,
  fontFamily: FONT.FAMILY.NANUMSQUARE,
  fontSize: FONT.SIZE.XS,
});

export const createPodMetricLineChartOption = ({
  podHistory,
  unit,
}: CreatePodMetricLineChartOptionInput): EChartsOption => {
  const seriesInput = createMetricChartSeries(podHistory);

  return {
    animation: false,
    tooltip: createTooltip(unit) as EChartsOption['tooltip'],
    grid: {
      left: 0,
      right: 0,
      top: 8,
      bottom: 0,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: [...seriesInput.xAxisLabels],
      axisLine: {
        show: true,
        lineStyle: {
          color: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL3,
        },
      },
      axisLabel: {
        ...createBaseAxisLabel(),
        align: 'left',
        interval: Math.floor((seriesInput.xAxisLabels.length - 1) / 4),
        color: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL5,
        fontFamily: FONT.FAMILY.NANUMSQUARE,
        fontSize: FONT.SIZE.SM,
        hideOverlap: false,
        margin: 12,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        ...createBaseAxisLabel(),
        margin: 12,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: SEMANTIC_COLOR.CONTAINER.STANDARD.ALT_LEVEL3,
          type: 'dashed',
          width: 1,
        },
      },
    },
    series: seriesInput.series.map((lineSeries) => ({
      type: 'line' as const,
      name: lineSeries.name,
      data: [...lineSeries.data],
      smooth: false,
      symbol: 'none',
      showSymbol: false,
      connectNulls: false,
      hoverAnimation: false,
      clip: true,
      lineStyle: {
        color: lineSeries.color,
        width: 1.25,
        cap: 'round' as const,
        join: 'round' as const,
      },
      itemStyle: {
        color: lineSeries.color,
      },
      emphasis: {
        disabled: true,
      },
    })),
  };
};

export const createPodMetricBarChartOption = ({
  podHistory,
  unit,
}: CreatePodMetricLineChartOptionInput): EChartsOption => {
  const podValues = podHistory
    .map((pod) => ({
      pod: pod.pod,
      value: pod.data[pod.data.length - 1]?.value ?? 0,
      color: pod.color,
    }))
    .sort((a, b) => b.value - a.value);

  return {
    animation: false,
    tooltip: createTooltip(unit) as EChartsOption['tooltip'],
    grid: {
      left: 0,
      right: 0,
      top: 8,
      bottom: 0,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: false,
      },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: podValues.map((pod) => pod.pod),
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        ...createBaseAxisLabel(),
        width: 98,
        overflow: 'truncate',
      },
    },
    series: [
      {
        type: 'bar',
        data: podValues.map((pod) => ({
          value: pod.value,
          itemStyle: {
            color: pod.color,
            borderRadius: [0, 4, 4, 0],
          },
        })),
        barWidth: 7,
        barCategoryGap: '40%',
        label: {
          show: true,
          position: 'right',
          color: SEMANTIC_COLOR.ON_CONTAINER.STANDARD.LEVEL5,
          fontFamily: FONT.FAMILY.NANUMSQUARE,
          fontSize: FONT.SIZE.XS,
          distance: 8,
          formatter: (params: unknown) => {
            const value = (params as { value?: unknown }).value;
            return formatMetricValue(typeof value === 'number' ? value : 0);
          },
        },
      },
    ],
  };
};
